import { fetchWithAuth } from '@/lib/fetchWithAuth';
import { 
  VendedorMetrics, 
  VendedorDetailedMetrics, 
  ParceiroSummary,
  VendedorFilters,
  CadastrarVendedorData,
  CadastrarVendedorResponse,
  AtualizarVendedorData,
  ToggleStatusResponse
} from '@/types/vendedores';

export class VendedoresService {
  
  /**
   * Busca lista de vendedores com métricas agregadas
   */
  static async getVendedores(filters?: VendedorFilters): Promise<VendedorMetrics[]> {
    try {
      const params = new URLSearchParams();
      
      if (filters?.periodo) params.append('periodo', filters.periodo);
      if (filters?.status) params.append('status', filters.status);
      if (filters?.ordenacao) params.append('ordenacao', filters.ordenacao);
      if (filters?.search) params.append('search', filters.search);
      
      const queryString = params.toString();
      const url = `/api/vendedores${queryString ? `?${queryString}` : ''}`;
      
      const response = await fetchWithAuth(url);
      
      if (!response.ok) {
        const error = await response.json();
        
        switch (response.status) {
          case 403:
            throw new Error('Acesso negado. Apenas administradores podem visualizar vendedores.');
          case 401:
            throw new Error('Usuário não autenticado');
          default:
            throw new Error(error.error || 'Erro ao buscar vendedores');
        }
      }
      
      return response.json();
    } catch (error) {
      console.error('Erro ao buscar vendedores:', error);
      throw error;
    }
  }
  
  /**
   * Busca métricas detalhadas de um vendedor
   */
  static async getVendedorDetails(vendedorId: string): Promise<VendedorDetailedMetrics> {
    try {
      const response = await fetchWithAuth(`/api/vendedores/${vendedorId}/metrics`);
      
      if (!response.ok) {
        const error = await response.json();
        
        switch (response.status) {
          case 404:
            throw new Error('Vendedor não encontrado');
          case 403:
            throw new Error('Acesso negado');
          case 401:
            throw new Error('Usuário não autenticado');
          default:
            throw new Error(error.error || 'Erro ao buscar detalhes do vendedor');
        }
      }
      
      const data = await response.json();
      
      // Transformar estrutura do backend para o formato esperado pelo tipo
      // Backend retorna: { vendedor: {...}, parceiros: [...], metrics_por_periodo: [...] }
      // Tipo espera: estrutura flat com vendedor_* + parceiros + metrics_por_periodo
      return {
        vendedor_id: data.vendedor.id,
        vendedor_nome: data.vendedor.nome,
        vendedor_telefone: data.vendedor.telefone,
        vendedor_email: data.vendedor.email,
        vendedor_cpf: data.vendedor.cpf,
        vendedor_pix: data.vendedor.pix,
        vendedor_estado: data.vendedor.estado,
        vendedor_cidade: data.vendedor.cidade,
        vendedor_tipo_pessoa: data.vendedor.tipo_pessoa,
        vendedor_cnpj: data.vendedor.cnpj,
        vendedor_razao_social: data.vendedor.razao_social,
        vendedor_nome_fantasia: data.vendedor.nome_fantasia,
        vendedor_cpf_representante: data.vendedor.cpf_representante,
        data_inicio: data.vendedor.created_at,
        status: data.vendedor.ativo,
        
        // Calcular métricas agregadas dos parceiros
        total_parceiros: data.parceiros.length,
        parceiros_ativos: data.parceiros.filter((p: ParceiroSummary) => p.ativo).length,
        parceiros_inativos: data.parceiros.filter((p: ParceiroSummary) => !p.ativo).length,
        parceiros_30_dias: 0, // Não está disponível na resposta detalhada
        
        // Agregar métricas dos parceiros
        total_leads: data.parceiros.reduce((sum: number, p: ParceiroSummary) => sum + p.total_leads, 0),
        leads_30_dias: 0, // Não disponível
        leads_7_dias: 0, // Não disponível
        
        propostas_enviadas: data.parceiros.reduce((sum: number, p: ParceiroSummary) => sum + p.propostas_enviadas, 0),
        propostas_30_dias: 0, // Não disponível
        valor_propostas: 0, // Não disponível
        valor_propostas_30_dias: 0, // Não disponível
        
        contratos_fechados: data.parceiros.reduce((sum: number, p: ParceiroSummary) => sum + p.contratos_fechados, 0),
        contratos_30_dias: 0, // Não disponível
        valor_contratos: data.parceiros.reduce((sum: number, p: ParceiroSummary) => sum + p.valor_contratos, 0),
        valor_contratos_30_dias: 0, // Não disponível
        
        // Calcular taxas de conversão
        taxa_conversao_leads_contratos: data.parceiros.length > 0 
          ? Number((data.parceiros.reduce((sum: number, p: ParceiroSummary) => sum + p.taxa_conversao, 0) / data.parceiros.length).toFixed(2))
          : 0,
        taxa_conversao_leads_propostas: 0, // Não disponível
        
        // Encontrar melhor parceiro
        melhor_parceiro_id: data.parceiros.length > 0 
          ? data.parceiros.reduce((best: ParceiroSummary, p: ParceiroSummary) => 
              p.contratos_fechados > best.contratos_fechados ? p : best
            ).id 
          : undefined,
        melhor_parceiro_nome: data.parceiros.length > 0 
          ? data.parceiros.reduce((best: ParceiroSummary, p: ParceiroSummary) => 
              p.contratos_fechados > best.contratos_fechados ? p : best
            ).nome 
          : undefined,
        melhor_parceiro_contratos: data.parceiros.length > 0 
          ? Math.max(...data.parceiros.map((p: ParceiroSummary) => p.contratos_fechados))
          : 0,
        
        // Incluir arrays originais
        parceiros: data.parceiros,
        metrics_por_periodo: data.metrics_por_periodo
      };
    } catch (error) {
      console.error('Erro ao buscar detalhes do vendedor:', error);
      throw error;
    }
  }
  
  /**
   * Busca parceiros de um vendedor
   */
  static async getVendedorParceiros(vendedorId: string, filters?: {
    periodo?: string;
    status?: string;
    search?: string;
  }): Promise<ParceiroSummary[]> {
    try {
      const params = new URLSearchParams();
      
      if (filters?.periodo) params.append('periodo', filters.periodo);
      if (filters?.status) params.append('status', filters.status);
      if (filters?.search) params.append('search', filters.search);
      
      const queryString = params.toString();
      const url = `/api/vendedores/${vendedorId}/parceiros${queryString ? `?${queryString}` : ''}`;
      
      const response = await fetchWithAuth(url);
      
      if (!response.ok) {
        const error = await response.json();
        
        switch (response.status) {
          case 404:
            throw new Error('Vendedor não encontrado');
          case 403:
            throw new Error('Acesso negado');
          case 401:
            throw new Error('Usuário não autenticado');
          default:
            throw new Error(error.error || 'Erro ao buscar parceiros do vendedor');
        }
      }
      
      return response.json();
    } catch (error) {
      console.error('Erro ao buscar parceiros do vendedor:', error);
      throw error;
    }
  }
  
  /**
   * Cria um novo vendedor
   */
  static async createVendedor(data: CadastrarVendedorData): Promise<CadastrarVendedorResponse> {
    try {
      const response = await fetchWithAuth('/api/vendedores', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
      
      if (!response.ok) {
        const error = await response.json();
        
        // Tratamento de erros específicos
        switch (response.status) {
          case 400:
            throw new Error(error.error || 'Dados inválidos');
          case 403:
            throw new Error('Sem permissão para criar vendedor');
          case 409:
            throw new Error('Email já cadastrado no sistema');
          case 500:
            throw new Error('Erro interno do servidor. Tente novamente.');
          default:
            throw new Error(error.error || 'Erro ao cadastrar vendedor');
        }
      }
      
      return response.json();
    } catch (error) {
      console.error('Erro ao cadastrar vendedor:', error);
      throw error;
    }
  }
  
  /**
   * Atualiza dados de um vendedor
   */
  static async updateVendedor(id: string, data: AtualizarVendedorData) {
    try {
      const response = await fetchWithAuth(`/api/vendedores/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
      
      if (!response.ok) {
        const error = await response.json();
        
        switch (response.status) {
          case 400:
            throw new Error(error.error || 'Dados inválidos');
          case 403:
            throw new Error('Sem permissão para editar vendedor');
          case 404:
            throw new Error('Vendedor não encontrado');
          default:
            throw new Error(error.error || 'Erro ao atualizar vendedor');
        }
      }
      
      return response.json();
    } catch (error) {
      console.error('Erro ao atualizar vendedor:', error);
      throw error;
    }
  }
  
  /**
   * Ativa/Desativa um vendedor
   * ATENÇÃO: Retorna erro se houver parceiros ativos
   */
  static async toggleVendedorStatus(id: string): Promise<ToggleStatusResponse> {
    try {
      const response = await fetchWithAuth(`/api/vendedores/${id}/status`, {
        method: 'PUT'
      });
      
      if (!response.ok) {
        const error = await response.json();
        
        // Tratamento especial para vendedor com parceiros ativos
        if (response.status === 400 && error.parceiros_ativos) {
          throw new Error(
            `Não é possível desativar vendedor com ${error.parceiros_ativos} parceiros ativos. ` +
            `Reatribua os parceiros primeiro.`
          );
        }
        
        switch (response.status) {
          case 403:
            throw new Error('Sem permissão para alterar status');
          case 404:
            throw new Error('Vendedor não encontrado');
          default:
            throw new Error(error.error || 'Erro ao alterar status');
        }
      }
      
      return response.json();
    } catch (error) {
      console.error('Erro ao alterar status do vendedor:', error);
      throw error;
    }
  }

  /**
   * Reseta a senha do vendedor para senha provisória padrão
   * Permissão: Apenas Admin
   * Útil para ambiente de teste quando a senha foi esquecida
   */
  static async resetVendedorSenha(id: string) {
    try {
      const response = await fetchWithAuth(`/api/vendedores/${id}/reset-senha`, {
        method: 'POST'
      });
      
      if (!response.ok) {
        const error = await response.json();
        
        switch (response.status) {
          case 403:
            throw new Error('Sem permissão para resetar senha');
          case 404:
            throw new Error('Vendedor não encontrado');
          default:
            throw new Error(error.error || 'Erro ao resetar senha');
        }
      }
      
      return response.json();
    } catch (error) {
      console.error('Erro ao resetar senha do vendedor:', error);
      throw error;
    }
  }
}

export const vendedoresService = VendedoresService;

